import type { CreatorScoreSnapshot } from '@/marketing/models/creatorScore';

export const SQUARE_VIEW_FORMULA =
  'hub-content70-internal30-selected-contentviews1000-square-aftr5-40-45-10-fixed05-20261021';

type DisplayUnit = 'score' | 'views' | 'factor' | 'points';

/** Accept recorded finite values, with the formula's bounded domain for factors. */
function isRecordedNumber(value: unknown, unit: DisplayUnit): value is number {
  return typeof value === 'number' && Number.isFinite(value) && (unit !== 'factor' || (value >= 0 && value <= 1));
}

/** Format stored evidence without deriving missing values. */
function displayValue(value: unknown, unit: DisplayUnit): string {
  if (!isRecordedNumber(value, unit)) {
    return '—';
  }
  if (unit === 'factor') {
    return value.toLocaleString('en-US', { style: 'percent', maximumFractionDigits: 4 });
  }
  const number = value.toLocaleString('en-US', { maximumFractionDigits: 3 });
  return `${number}${unit === 'score' ? ' / 10' : ` ${unit}`}`;
}

/** Explain the matching persisted formula without recalculating the final score. */
export function SquareViewCalculationDetails({ score }: { score: CreatorScoreSnapshot }) {
  const calculation = score.calculation ?? {};
  const fields: { label: string; key: string; unit: DisplayUnit }[] = [
    { label: 'Original content', key: 'originalContentScore', unit: 'score' },
    { label: 'Mean views after exclusions', key: 'contentMeanViews', unit: 'views' },
    { label: 'Recorded view factor', key: 'viewMultiplier', unit: 'factor' },
    { label: 'Adjusted content', key: 'contentScore', unit: 'score' },
    { label: 'Internal score', key: 'internalScore', unit: 'score' },
    { label: 'Content contribution (70%)', key: 'contentContribution', unit: 'points' },
    { label: 'Internal contribution (30%)', key: 'internalContribution', unit: 'points' },
  ];
  const hasRecordedValues = fields.some(
    ({ key, unit }) => calculation[key] === null || isRecordedNumber(calculation[key], unit),
  );
  const reason = [calculation['naDetail'], calculation['naReason'], calculation['reason']].find(
    (value): value is string => typeof value === 'string' && value.trim().length > 0,
  );
  const mean = calculation['contentMeanViews'];

  return (
    <details className="tw-rounded tw-border tw-p-3">
      <summary className="tw-cursor-pointer tw-font-semibold">Calculation details · Content 70% + Internal 30%</summary>
      <div className="tw-mt-3 tw-space-y-3">
        <div className="tw-space-y-1">
          <p>View factor = min(1, (mean views / 1,000)²)</p>
          <p>Adjusted content = original content × view factor</p>
          <p>Base score = adjusted content × 70% + internal × 30%</p>
          <p className="tw-text-xs tw-text-muted-foreground">
            The factor applies once to the whole original content score and reaches its maximum at 1,000 views. Mean
            views use the retained posts after recorded pinned-post and view-outlier exclusions.
          </p>
        </div>
        {hasRecordedValues ? (
          <dl className="tw-space-y-1">
            {fields.map(({ label, key, unit }) => (
              <div key={key} className="tw-grid tw-grid-cols-2 tw-gap-3">
                <dt>{label}</dt>
                <dd className="tw-text-right tw-tabular-nums">{displayValue(calculation[key], unit)}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <p>No calculation breakdown is stored for this snapshot.</p>
        )}
        {(typeof mean !== 'number' || !Number.isFinite(mean)) && (
          <p className="tw-text-xs tw-text-muted-foreground">No recorded mean views are available in this snapshot.</p>
        )}
        <p className="tw-text-xs tw-text-muted-foreground">
          Internal weighting: Activity 5% · Completion 40% · Timeliness 45% · Repeat 10%. Only campaigns with verified
          selection history contribute. Missing internal evidence contributes zero; the content weight stays 70%.
        </p>
        {score.finalScore === 0.5 &&
          score.reviewStatus === 'blocked' &&
          (calculation['blacklistOverride'] === true || calculation['status'] === 'blacklist_fixed') && (
            <p className="tw-font-medium">Confirmed blacklist override: final score is fixed at 0.5 / 10.</p>
          )}
        {score.finalScore === null && <p>Final score unavailable{reason ? `: ${reason}` : '.'}</p>}
        <p className="tw-text-xs tw-text-muted-foreground">
          These values come from the stored snapshot. The final score is not recalculated here.
        </p>
      </div>
    </details>
  );
}
